import React, { useState, useEffect, useCallback } from "react";
import { PlayerProfile } from "../../store/supabasePlayerProfiles";
import { PlayerAvatar } from "../PlayerAvatar";
import {
  Team,
  SquadFormat,
  SquadWithPlayers,
  SquadRole,
  ensureSquad,
  getSquadWithPlayers,
  addPlayerToSquad,
  removePlayerFromSquad,
  setSquadRole,
} from "../../store/teams";

interface Props {
  team: Team;
  profiles: PlayerProfile[];
}

type RoleKey = "captain" | "vice_captain" | "wicketkeeper";

const FORMAT_LABELS: Record<SquadFormat, string> = {
  indoor: "Indoor",
  t20: "T20",
  odi: "ODI",
};

const ROLE_META: Record<RoleKey, { column: keyof SquadWithPlayers["squad"]; label: string; short: string; color: string }> = {
  captain: { column: "captain_player_id", label: "Captain", short: "C", color: "bg-yellow-500/20 text-yellow-300" },
  vice_captain: { column: "vice_captain_player_id", label: "Vice-Captain", short: "VC", color: "bg-orange-500/20 text-orange-300" },
  wicketkeeper: { column: "wicketkeeper_player_id", label: "Wicketkeeper", short: "WK", color: "bg-blue-500/20 text-blue-300" },
};

const TeamSquadManager: React.FC<Props> = ({ team, profiles }) => {
  const format = team.format;
  const formatLabel = FORMAT_LABELS[format];
  const [data, setData] = useState<SquadWithPlayers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSquadWithPlayers(team.id, format);
      setData(result);
    } catch (err: any) {
      setError(err?.message || "Failed to load squad");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [team.id, format]);

  useEffect(() => {
    load();
  }, [load]);

  const squadPlayerIds = new Set((data?.players || []).map((p) => p.id));
  const filteredProfiles = profiles.filter(
    (p) =>
      !squadPlayerIds.has(p.id) &&
      (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  async function handleAddMultiple() {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      const squad = await ensureSquad(team.id, format);
      for (const pid of selectedIds) {
        await addPlayerToSquad(squad.id, pid);
      }
      await load();
      showToast(`${selectedIds.size} player${selectedIds.size > 1 ? "s" : ""} added`);
      setSelectedIds(new Set());
      setShowPicker(false);
    } catch (err: any) {
      showToast(err?.message || "Failed to add players");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(profile: PlayerProfile) {
    if (!data) return;
    setBusy(true);
    try {
      const s = data.squad;
      const roleUpdates: { role: SquadRole; id: string | null }[] = [];
      if (s.captain_player_id === profile.id) roleUpdates.push({ role: "captain", id: null });
      if (s.vice_captain_player_id === profile.id) roleUpdates.push({ role: "vice_captain", id: null });
      if (s.wicketkeeper_player_id === profile.id) roleUpdates.push({ role: "wicketkeeper", id: null });
      for (const u of roleUpdates) {
        await setSquadRole(s.id, u.role, u.id);
      }
      await removePlayerFromSquad(s.id, profile.id);
      await load();
      showToast(`${profile.name} removed`);
    } catch (err: any) {
      showToast(err?.message || "Failed to remove player");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleRole(role: RoleKey, playerId: string) {
    if (!data) return;
    const s = data.squad;
    const currentId = s[ROLE_META[role].column] as string | null;
    const nextId = currentId === playerId ? null : playerId;

    if (nextId) {
      if (role === "captain" && s.vice_captain_player_id === nextId) {
        showToast("Captain and Vice-Captain must be different players");
        return;
      }
      if (role === "vice_captain" && s.captain_player_id === nextId) {
        showToast("Captain and Vice-Captain must be different players");
        return;
      }
    }

    setBusy(true);
    try {
      const squadRole: SquadRole =
        role === "captain" ? "captain" : role === "vice_captain" ? "vice_captain" : "wicketkeeper";
      await setSquadRole(s.id, squadRole, nextId);
      await load();
    } catch (err: any) {
      showToast(err?.message || "Failed to update role");
    } finally {
      setBusy(false);
    }
  }

  function roleBadgesFor(playerId: string): RoleKey[] {
    if (!data) return [];
    const s = data.squad;
    const badges: RoleKey[] = [];
    if (s.captain_player_id === playerId) badges.push("captain");
    if (s.vice_captain_player_id === playerId) badges.push("vice_captain");
    if (s.wicketkeeper_player_id === playerId) badges.push("wicketkeeper");
    return badges;
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-900/40 border border-red-700/50 rounded-lg p-4 text-red-200 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="underline font-semibold">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-green-300">Loading squad...</div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-green-200 text-sm uppercase tracking-wide">
              Current {formatLabel} Squad ({data?.players.length || 0})
            </h4>
            <button
              onClick={() => {
                setSearch("");
                setSelectedIds(new Set());
                setShowPicker(true);
              }}
              className="bg-green-600 hover:bg-green-700 px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              + Add Players
            </button>
          </div>

          {!data || data.players.length === 0 ? (
            <div className="bg-green-950/40 rounded-xl p-8 text-center border border-green-800/40">
              <p className="text-green-300 mb-1">No players in this squad yet</p>
              <p className="text-green-400/60 text-sm">
                Add players from your Player Profiles to build the {formatLabel} squad.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.players.map((p) => {
                const badges = roleBadgesFor(p.id);
                return (
                  <div
                    key={p.id}
                    className="bg-green-950/50 rounded-lg p-3 border border-green-800/40"
                  >
                    <div className="flex items-center gap-3">
                      <PlayerAvatar name={p.name} photoUrl={p.photo_url} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white truncate flex items-center gap-1.5">
                          {p.name}
                          {badges.map((b) => (
                            <span
                              key={b}
                              className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${ROLE_META[b].color}`}
                            >
                              {ROLE_META[b].short}
                            </span>
                          ))}
                        </div>
                        <div className="text-xs text-green-400/70">{p.role || "Player"}</div>
                      </div>
                      <button
                        onClick={() => handleRemove(p)}
                        disabled={busy}
                        className="text-red-300 hover:text-red-200 text-sm px-2 py-1 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <RoleToggle
                        active={data.squad.captain_player_id === p.id}
                        label="Captain"
                        onClick={() => handleToggleRole("captain", p.id)}
                        disabled={busy}
                      />
                      <RoleToggle
                        active={data.squad.vice_captain_player_id === p.id}
                        label="Vice-Captain"
                        onClick={() => handleToggleRole("vice_captain", p.id)}
                        disabled={busy}
                      />
                      <RoleToggle
                        active={data.squad.wicketkeeper_player_id === p.id}
                        label="Keeper"
                        onClick={() => handleToggleRole("wicketkeeper", p.id)}
                        disabled={busy}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showPicker && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-lg w-full max-h-[80vh] flex flex-col shadow-2xl">
            <div className="border-b border-slate-700 px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white">Add Players</h3>
                <button
                  onClick={() => setShowPicker(false)}
                  className="text-2xl text-slate-300 hover:bg-slate-700 rounded-lg px-2"
                >
                  &times;
                </button>
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search players by name..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-500"
                autoFocus
              />
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {filteredProfiles.length === 0 ? (
                <div className="text-center py-10 text-slate-300">
                  {profiles.length === 0
                    ? "No player profiles yet. Create players in the Player Profiles section first."
                    : search
                    ? "No players match your search"
                    : "All your players are already in this squad"}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredProfiles.map((p) => {
                    const selected = selectedIds.has(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => toggleSelect(p.id)}
                        className={`w-full text-left bg-slate-900 rounded-lg p-3 flex items-center gap-3 border transition-colors ${
                          selected
                            ? "border-green-500 bg-green-900/20"
                            : "border-slate-700 hover:border-slate-600"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            selected
                              ? "border-green-500 bg-green-500"
                              : "border-slate-600"
                          }`}
                        >
                          {selected && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <PlayerAvatar name={p.name} photoUrl={p.photo_url} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-white truncate">{p.name}</div>
                          <div className="text-xs text-slate-400">{p.role || "Player"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {selectedIds.size > 0 && (
              <div className="border-t border-slate-700 p-4">
                <button
                  onClick={handleAddMultiple}
                  disabled={busy}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-900 disabled:cursor-not-allowed px-4 py-3 rounded-lg font-semibold text-white transition-colors"
                >
                  {busy ? "Adding..." : `Add ${selectedIds.size} Player${selectedIds.size > 1 ? "s" : ""}`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed top-4 right-4 z-[60] bg-green-600 text-white px-5 py-3 rounded-lg shadow-2xl font-medium">
          {toast}
        </div>
      )}
    </div>
  );
};

const RoleToggle: React.FC<{ active: boolean; label: string; onClick: () => void; disabled?: boolean }> = ({
  active,
  label,
  onClick,
  disabled,
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`text-xs px-2.5 py-1 rounded-md font-semibold transition-colors disabled:opacity-50 ${
      active
        ? "bg-green-600 text-white"
        : "bg-green-900/40 text-green-300 hover:bg-green-800/60 border border-green-800/50"
    }`}
  >
    {label}
  </button>
);

export default TeamSquadManager;
